import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import ConfirmModal from "../../components/ConfirmModal";
import SuccessModal from "../../components/SuccessModal";
import {
  paginateEducationalResources,
  deleteEducationalResource,
  EducationalResourcePaginationPayload,
} from "../../api/educationalResource";
import { useNavigate } from "react-router-dom";
import { getPlayableSource } from "../../utils/videoUrl";

interface EducationalResourceItem {
  id: string;
  title: string;
  description: string;
  category: string;
  clinicId: string;
  video?: { url?: string } | string;
  videoLink?: string;
  videolink?: string;
  createdOn?: string;
}

const defaultPayload: EducationalResourcePaginationPayload = {
  curPage: 1,
  perPage: 10,
  sortBy: "created_on",
  direction: "desc",
  whereClause: [],
};

const EducationalResourceList: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<EducationalResourceItem[]>([]);
  const [total, setTotal] = useState(0);
  const [payload, setPayload] =
    useState<EducationalResourcePaginationPayload>(defaultPayload);
  const [search, setSearch] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  // Video player modal
  const [playerVideo, setPlayerVideo] = useState<{
    title: string;
    category?: string;
    type: "youtube" | "file";
    url: string;
  } | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement>>({});
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const navigate = useNavigate();
  const fetchedRef = useRef(false);
  const lastFetchKeyRef = useRef<string>("");

  const totalPages = Math.max(1, Math.ceil(total / payload.perPage));

  const formatCategory = (raw?: string) => {
    if (!raw) return "—";
    const normalized = raw.toLowerCase().replace(/_/g, " ");
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const body: EducationalResourcePaginationPayload = {
        ...payload,
        whereClause: search
          ? [{ key: "category", value: search, operator: "=" }]
          : [],
      };
      const res = await paginateEducationalResources(body);
      const data = res?.data || {};
      setItems(Array.isArray(data.data) ? data.data : []);
      setTotal(data.total || data.count || 0);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const key = JSON.stringify({
      p: payload.curPage,
      pp: payload.perPage,
      s: payload.sortBy,
      d: payload.direction,
      q: search,
    });
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    fetchList();
  }, [
    payload.curPage,
    payload.perPage,
    payload.sortBy,
    payload.direction,
    search,
  ]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside the open menu and button
      const isClickOnButton = Object.values(buttonRefs.current).some(
        (btn) => btn && btn.contains(target)
      );
      const isClickOnMenu = target.closest('.dropdown-menu-container');

      if (!isClickOnButton && !isClickOnMenu && openMenuId) {
        setOpenMenuId(null);
        setDropdownPosition(null);
      }
    };

    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  const formatDate = (date?: string) => {
    if (!date) return "—";
    try {
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "—";
    }
  };

  const requestDelete = (id: string) => {
    setDeleteId(id);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setLoading(true);
    try {
      await deleteEducationalResource(deleteId);
      fetchList();
      setShowSuccess(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Delete failed");
    } finally {
      setShowConfirm(false);
      setDeleteId(null);
      setLoading(false);
    }
  };

  const handleView = (id: string) => {
    navigate(`/educational-resources/create/${id}`);
  };

  const goToPage = (p: number) => {
    setPayload((prev) => ({
      ...prev,
      curPage: Math.min(Math.max(1, p), totalPages),
    }));
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}

      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-6 md:p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                    Educational Hub
                  </h2>
                  <p className="text-gray-500 mt-2">
                    Manage educational videos and content
                  </p>
                </div>
                <a
                  href="/educational-resources/create"
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  + Create Resource
                </a>
              </div>

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    Total: <span className="font-medium">{total}</span>
                  </span>

                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Category:</span>
                  <div className="w-full md:w-64">
                    <select
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPayload((p) => ({ ...p, curPage: 1 }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                    >
                      <option value="">All categories</option>
                      <option value="BRACES">Braces</option>
                      <option value="ALIGNERS">Aligners</option>
                      <option value="PROCEDURES">Procedures</option>
                      <option value="DENTAL_CARE">Dental care</option>
                      <option value="RECOMMENDATIONS">Recommendations</option>
                    </select>
                  </div>
                  <span className="text-sm text-gray-600">Rows:</span>
                  <select
                    className="border rounded-lg p-1 select-plain"
                    value={payload.perPage}
                    onChange={(e) =>
                      setPayload((p) => ({
                        ...p,
                        perPage: parseInt(e.target.value, 10) || 10,
                        curPage: 1,
                      }))
                    }
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Title
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Category
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Created
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {items.length === 0 ? (
                      <tr>
                        <td
                          className="px-4 py-6 text-center text-red-500"
                          colSpan={4}
                        >
                          No records found
                        </td>
                      </tr>
                    ) : (
                      items.map((it) => (
                        <tr key={it.id}>
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {it.title}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {formatCategory(it.category)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {formatDate(it.createdOn)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div 
                              className="relative" 
                              ref={(el) => {
                                if (openMenuId === it.id) {
                                  (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                                }
                              }}
                            >
                              <button
                              ref={(el) => {
                                if (el) {
                                  buttonRefs.current[it.id] = el;
                                }
                              }}
                                className="text-gray-600 hover:text-gray-900 focus:outline-none p-1 rounded-full hover:bg-gray-100"
                              onClick={(e) => {
                                if (openMenuId === it.id) {
                                  setOpenMenuId(null);
                                  setDropdownPosition(null);
                                } else {
                                  const button = e.currentTarget;
                                  const rect = button.getBoundingClientRect();
                                  setDropdownPosition({
                                    top: rect.bottom + window.scrollY + 4,
                                    left: rect.right + 4,
                                  });
                                  setOpenMenuId(it.id);
                                }
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <circle cx="12" cy="5" r="2" />
                                  <circle cx="12" cy="12" r="2" />
                                  <circle cx="12" cy="19" r="2" />
                                </svg>
                              </button>
                              {openMenuId === it.id && dropdownPosition && createPortal(
                                <div
                                  ref={menuRef}
                                  className="dropdown-menu-container fixed w-32 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] overflow-hidden"
                                  style={{
                                    top: `${dropdownPosition.top}px`,
                                    left: `${dropdownPosition.left}px`,
                                  }}
                                >
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                    disabled={!getPlayableSource(it)}
                                    onClick={() => {
                                      const src = getPlayableSource(it);
                                      if (src) {
                                        setPlayerVideo({ title: it.title, category: it.category, ...src });
                                      } else {
                                        toast.info("No video available for this resource");
                                      }
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Watch
                                  </button>
                                  <div className="border-t border-gray-200"></div>
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      handleView(it.id);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <div className="border-t border-gray-200"></div>
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      requestDelete(it.id);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>,
                                document.body
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <hr />
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-600">
                  Page {payload.curPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(1)}
                    disabled={payload.curPage === 1}
                  >
                    « First
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(payload.curPage - 1)}
                    disabled={payload.curPage === 1}
                  >
                    ‹ Prev
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(payload.curPage + 1)}
                    disabled={payload.curPage >= totalPages}
                  >
                    Next ›
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(totalPages)}
                    disabled={payload.curPage >= totalPages}
                  >
                    Last »
                  </button>
                </div>
              </div>

              <ConfirmModal
                isOpen={showConfirm}
                onCancel={() => setShowConfirm(false)}
                onConfirm={confirmDelete}
                title="Delete Resource"
                message="Are you sure you want to delete this resource?"
                confirmText="Delete"
                cancelText="Cancel"
                loading={loading}
                variant="danger"
              />

              <SuccessModal
                isOpen={showSuccess}
                title="Resource Deleted"
                message="The educational resource has been deleted successfully."
                onClose={() => setShowSuccess(false)}
              />

              {/* Video player modal */}
              {playerVideo &&
                createPortal(
                  <div
                    className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
                    onClick={() => setPlayerVideo(null)}
                  >
                    <div
                      className="relative w-full max-w-2xl bg-white rounded-2xl overflow-hidden shadow-2xl p-4"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="text-base font-semibold text-gray-800">
                            {playerVideo.title}
                          </h3>
                          {playerVideo.category && (
                            <p className="text-[11px] text-blue-600">
                              {formatCategory(playerVideo.category)}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                          onClick={() => setPlayerVideo(null)}
                          aria-label="Close video"
                        >
                          &times;
                        </button>
                      </div>
                      <div
                        className="relative w-full rounded-xl overflow-hidden bg-black"
                        style={{ paddingTop: "56.25%" }}
                      >
                        {playerVideo.type === "youtube" ? (
                          <iframe
                            className="absolute inset-0 w-full h-full"
                            src={`${playerVideo.url}?autoplay=1&rel=0`}
                            title={playerVideo.title}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <video
                            className="absolute inset-0 w-full h-full bg-black"
                            src={playerVideo.url}
                            controls
                            autoPlay
                          />
                        )}
                      </div>
                    </div>
                  </div>,
                  document.body
                )}
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default EducationalResourceList;
